import type { TProvisioningStatus } from '@conversed/shared/src/central/types/provisioning.type';
import {
  STUDIO_TYPE_LABELS,
  StudioType,
} from '@conversed/shared/src/central/types/studio.type';
import aiChat from '@images/home/ai-chat.png';
import aiTicketDeflection from '@images/home/ai-ticket-deflection.png';
import suggestedEmail from '@images/home/suggested-email.png';
import voiceAi from '@images/home/voice-ai.png';
import imageNotFound from '@images/image-not-found.svg';
import clsx from 'clsx';
import type React from 'react';
import { useTranslation } from 'react-i18next';

const STUDIO_TYPE_ICONS: Record<StudioType, string> = {
  [StudioType.CHAT_AI]: aiChat,
  [StudioType.VOICE_AI]: voiceAi,
  [StudioType.TICKET_AI]: aiTicketDeflection,
  [StudioType.EMAIL_AI]: suggestedEmail,
};

// * Coarse superAdmin-only provisioning indicator. 'completed' carries no badge
// * (the company is just active); returns null for it and for an absent status.
const getProvisionBadge = (
  provisionStatus?: TProvisioningStatus,
): { labelKey: string; className: string } | null => {
  if (provisionStatus === 'in_progress') {
    return {
      labelKey: 'provisioning.badge.in-progress',
      className: 'bg-blue-100 text-blue-700',
    };
  }

  if (provisionStatus === 'failed') {
    return {
      labelKey: 'provisioning.badge.failed',
      className: 'bg-red-100 text-red-700',
    };
  }

  if (provisionStatus === 'not_started') {
    return {
      labelKey: 'provisioning.badge.not-started',
      className: 'bg-gray-100 text-gray-500',
    };
  }

  if (provisionStatus === 'deprovisioned') {
    return {
      labelKey: 'provisioning.badge.deprovisioned',
      className: 'bg-gray-200 text-gray-600',
    };
  }

  return null;
};

interface Props {
  iconUrl: string;
  name: string;
  studioType?: StudioType;
  provisionStatus?: TProvisioningStatus;
  onPress: () => void;
}

export const CompanyCard: React.FC<Props> = ({
  iconUrl,
  name,
  studioType,
  provisionStatus,
  onPress,
}) => {
  const { t } = useTranslation();
  const studioLabel = studioType
    ? STUDIO_TYPE_LABELS[studioType]
    : STUDIO_TYPE_LABELS[StudioType.CHAT_AI];
  const studioIcon = studioType
    ? STUDIO_TYPE_ICONS[studioType]
    : STUDIO_TYPE_ICONS[StudioType.CHAT_AI];
  const provisionBadge = getProvisionBadge(provisionStatus);

  return (
    <div
      className='flex flex-row items-center mx-3 my-2 bg-white p-0 shadow-card rounded-lg cursor-pointer hover:shadow-lg transition-shadow'
      onClick={onPress}>
      <div className='w-[60px] h-[60px] min-w-[60px] flex items-center justify-center mx-3 my-2'>
        <img
          className='max-h-[60px]'
          src={iconUrl || imageNotFound}
          alt='company-card-icon'
          onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
            (e.target as HTMLImageElement).onerror = null;
            (e.target as HTMLImageElement).src = imageNotFound;
          }}
        />
      </div>
      <div className='flex-1'>
        <div className='flex items-center gap-2'>
          <h4 className='text-primary-base text-lg font-medium'>{name}</h4>
          {provisionBadge && (
            <span
              className={clsx(
                'text-pico font-medium px-2 py-0.5 rounded-full',
                provisionBadge.className,
              )}>
              {t(provisionBadge.labelKey)}
            </span>
          )}
        </div>
        <div className='flex items-center gap-1.5'>
          <img src={studioIcon} alt={studioLabel} className='w-4 h-4' />
          <span className='text-xs text-gray-600'>{studioLabel}</span>
        </div>
      </div>
    </div>
  );
};

export default CompanyCard;
